import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Grid, Paper, IconButton } from '@mui/material';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import ModalForms from '../Shared/Modals/index.js';
import { AiActions } from '../../Services/_actions/index.js';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import { useNavigate } from 'react-router';
import HomeIcon from '@mui/icons-material/Home';
import RichTextEditor from '../Shared/TextEditor/index.tsx';
import CampaignDropdown from '../Shared/CampaignDropdown/index.tsx';
import LoadingButton from '../Shared/Loading/index.tsx';

const InstructionSet: React.FC = () => {
  const [instructionFilters, setInstructionFilters] = useState({
    organisation: null,
    campaign: null,
  });
  const [editedInstruction, setEditedInstruction] = useState('');
  const [instructionExists, setInstructionExists] = useState(false);
  const aiActions = AiActions();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const handleDocumentLoad = async (filterObj: any) => {
    if (!filterObj?.organisation?.organisation_id) {
      setEditedInstruction('');
      return;
    }

    try {
      const payload = {
        organisation_id: filterObj?.organisation?.organisation_id,
        campaign_id: filterObj?.campaign?.proposal_id ?? "",
      };

      const data = await aiActions.getInstructionSet(payload);

      if (data?.instruction) {
        setEditedInstruction(data.instruction);
        setInstructionExists(true); // ✅ clearly mark it as existing
        showSnackBar('Instruction Set loaded successfully!', 'success');
      } else {
        setInstructionExists(false); // ✅ no instruction means treat as new
        setEditedInstruction('');
        showSnackBar('No instruction found for this organisation.', 'warning');
      }
    } catch (err: any) {
      setInstructionExists(false); // ✅ error case should also mark as not existing
      setEditedInstruction('');
      showSnackBar(err?.message || 'Failed to load instruction set.', 'warning');
    }
  };


  const handleSave = async () => {
    if (!instructionFilters?.organisation?.organisation_id) {
      showSnackBar('Please select an organisation first.', 'warning');
      return;
    }
    setIsSaving(true);
    try {
      let payload = {
        organisation_id: instructionFilters?.organisation?.organisation_id,
        campaign_id: instructionFilters?.campaign?.proposal_id ?? '',
        instruction: editedInstruction,
      };

      if (instructionExists) {
        await aiActions.updateInstructionSet(payload, editedInstruction);
        showSnackBar('Instruction Set updated successfully!', 'success');
      } else {
        await aiActions.postInstructionSet(payload, editedInstruction);
        showSnackBar('Instruction Set created successfully!', 'success');
        setInstructionExists(true); // Flag now exists
      }
    } catch (err) {
      showSnackBar('Error saving instruction set.', 'error');
    } finally {
      setIsSaving(false)
    }
  };

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Instruction Set' },
  ];

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 2 }}>
        <DynamicBreadcrumbs
          items={breadcrumbsData}
          onBreadcrumbClick={(event, item: { label: string; href: string }) => {
            if (item?.href) navigate(item.href);
          }}
        />

        <Grid container spacing={2} alignItems="center" mb={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" fontWeight={600}>
              Editable Instruction
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={1}>
              {!instructionFilters?.organisation?.organisation_id ? (
                // OrganisationDropdown on right side when not selected
                <>
                  <Grid item xs={12} md={6}></Grid> {/* Empty left side */}
                  <Grid item xs={12} md={6}>
                    <OrganisationDropdown
                      label="Select Organisation"
                      value={instructionFilters?.organisation ?? null}
                      onChange={(val) => {
                        setInstructionFilters({
                          ...instructionFilters,
                          organisation: val,
                          campaign: null,
                        });
                        handleDocumentLoad({ ...instructionFilters, organisation: val });
                      }}
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                </>
              ) : (
                // Both dropdowns side by side when organisation is selected
                <>
                  <Grid item xs={12} md={6}>
                    <OrganisationDropdown
                      label="Select Organisation"
                      value={instructionFilters?.organisation ?? null}
                      onChange={(val) => {
                        setInstructionFilters({
                          ...instructionFilters,
                          organisation: val,
                          campaign: null,
                        });
                        handleDocumentLoad({ ...instructionFilters, organisation: val });
                      }}
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CampaignDropdown
                      label="Campaign"
                      value={instructionFilters?.campaign ?? null}
                      onChange={(value) => {
                        setInstructionFilters({ ...instructionFilters, campaign: value });
                        handleDocumentLoad({ ...instructionFilters, campaign: value });
                      }}
                      organisationId={instructionFilters?.organisation?.organisation_id}
                      sx={{ width: '100%' }}
                      error={false}
                      errorMessage=""
                      disabled={false}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Box mt={2}>
          <RichTextEditor
            label="Editable Instruction Set"
            value={editedInstruction}
            onChange={setEditedInstruction}
            height="250px"
          />
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <LoadingButton
              buttonName='Save Instructions'
              onClickButton={() => { handleSave() }}
              isLoading={isSaving}
              size='small'
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default InstructionSet;
